import React, { useEffect, useRef, useState } from 'react';
import { RING, C } from '../portalkit';

const RATE_MS = 120; // per character, from the handoff

/**
 * POSTER + LOG — the black side panel (design_handoff_auth_profile_planner §5). The poster sits
 * blurred and grey until the plan is ready, then clears over 600ms with a gradient caption. Under
 * it the latest five log lines, typed on at 120 ms/char one at a time (newest white, older grey,
 * host notes pastel pink) with a blinking cyan caret while the plan works; then the progress bar,
 * a RING fill that widens over 600ms.
 *
 * `entries` = [{ t, text, kind }] from plannerModel.logEntries, append-only.
 */
export default function PlannerLog({ entries = [], working = false, ready = false, posterUrl = null, title, meta, progressLabel, percent = 0, reduced = false, aspect = '4 / 5', onPreview = null, previewEnabled = false, keep = 5 }) {
  const [shown, setShown] = useState([]);       // fully typed entries
  const [typing, setTyping] = useState(null);   // { entry, chars }
  const queue = useRef([]);
  const seen = useRef(0);
  const frame = useRef(null);

  useEffect(() => {
    if (entries.length < seen.current) { seen.current = 0; setShown([]); queue.current = []; setTyping(null); }
    for (let i = seen.current; i < entries.length; i++) queue.current.push(entries[i]);
    seen.current = entries.length;
  }, [entries]);

  useEffect(() => {
    let last = 0; let acc = 0;
    const tick = (t) => {
      if (!last) last = t;
      const dt = t - last; last = t;
      setTyping((cur) => {
        if (!cur) {
          if (queue.current.length === 0) return null;
          const entry = queue.current.shift();
          if (reduced) { setShown((s) => [...s, entry]); return null; }
          acc = 0;
          return { entry, chars: 0 };
        }
        acc += dt;
        const step = Math.floor(acc / RATE_MS);
        if (step <= 0) return cur;
        acc -= step * RATE_MS;
        const chars = Math.min(cur.entry.text.length, cur.chars + step);
        if (chars >= cur.entry.text.length) { setShown((s) => [...s, cur.entry]); return null; }
        return { entry: cur.entry, chars };
      });
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [reduced]);

  const visible = [...shown.slice(-(keep - (typing ? 1 : 0))), ...(typing ? [{ ...typing.entry, text: typing.entry.text.slice(0, typing.chars) }] : [])];
  const colour = (e, newest) => (e.kind === 'note' ? C.pastelPink : newest ? '#fff' : C.onBlack2);

  return (
    <aside data-testid="planner-log" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 28, background: '#000', color: '#fff', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 14px 14px 20px' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>POSTER + LOG</span>
        {onPreview && (
          <button type="button" onClick={onPreview} disabled={!previewEnabled} data-testid="preview-event" className="pk pk-btn" style={{ height: 34, padding: '0 14px', background: '#fff', color: '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', opacity: previewEnabled ? 1 : 0.35 }}>PREVIEW EVENT ↗</button>
        )}
      </div>
      <div style={{ position: 'relative', aspectRatio: aspect, margin: '0 8px', overflow: 'hidden', borderRadius: 20, background: C.surfaceDark }}>
        {posterUrl && <img src={posterUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: ready ? 1 : 0.25, filter: ready ? 'none' : 'blur(12px) grayscale(1)', transition: 'opacity .6s, filter .6s' }} />}
        {!posterUrl && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(70% 60% at 50% 40%, rgba(181,177,255,.18), rgba(0,0,0,0) 70%)', opacity: ready ? 1 : 0.5, transition: 'opacity .6s' }} />
        )}
        {ready && (title || meta) && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '60px 16px 14px', background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.85))' }}>
            {title && <span style={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>{title}</span>}
            {meta && <span style={{ display: 'block', marginTop: 6, fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.onBlack3, textTransform: 'uppercase' }}>{meta}</span>}
          </div>
        )}
      </div>
      <div aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 20px 10px', fontSize: 11, letterSpacing: '.08em', lineHeight: 1.5, minHeight: 96 }}>
        {visible.map((e, i) => {
          const newest = i === visible.length - 1;
          return (
            <div key={`${e.t}-${e.text.slice(0, 16)}-${i}`} style={{ display: 'flex', gap: 10, color: colour(e, newest), wordBreak: 'break-word' }}>
              <span style={{ flexShrink: 0, color: C.onBlack4, minWidth: 30 }}>{e.t}</span>
              <span>{e.text}{newest && typing && !reduced && <span className="pl-caret" style={{ marginLeft: 4 }} />}</span>
            </div>
          );
        })}
        {working && !typing && <span className="pl-caret" data-motion="1" />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 20px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>
          <span>{progressLabel}</span><span style={{ color: C.onBlack2 }}>{Math.round(percent)}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: C.hairDark, overflow: 'hidden' }}>
          <div data-motion="1" className="pk-ring-anim" style={{ height: '100%', width: `${Math.max(0, Math.min(100, percent))}%`, borderRadius: 999, background: RING, backgroundSize: '200% 100%', transition: 'width .6s' }} />
        </div>
      </div>
    </aside>
  );
}
